import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
    Divider,
    FormHelperText,
    IconButton,
    InputAdornment,
    Typography,
} from '@mui/material'
import Button from '@mui/material/Button'
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik'
import React from 'react'
import config from 'config'
import { StyledLink } from 'shared/components/Typography'
import { LoginFormValues } from 'shared/models/user'
import { commonRouteConfig } from 'shared/routeConfig'
import { getSubdomain } from 'shared/utils/routing'
import * as Yup from 'yup'
import {
    StyledButtonContainer,
    StyledSubtext,
    StyledTextInput,
    StyledTextInputWithSubtext,
} from '../styled'

const initialFormValues = {
    email: '',
    password: '',
}

export interface LoginFormProps {
    onSubmit: (
        values: LoginFormValues,
        helpers: FormikHelpers<LoginFormValues>
    ) => Promise<void | null>
}

const LoginForm: React.FC<LoginFormProps> = ({ onSubmit }) => {
    const loginValidationSchema = Yup.object().shape({
        email: Yup.string()
            .matches(
                /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                `Invalid email format`
            )
            .required('Required'),
        password: Yup.string().required('Required'),
    })

    const [showPassword, setShowPassword] = React.useState(false)

    const handleClickShowPassword = () => setShowPassword((show) => !show)

    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault()
    }

    const subdomain = getSubdomain(process.env.REACT_APP_DOMAIN)

    return (
        <React.Fragment>
            <Formik
                initialValues={initialFormValues}
                onSubmit={onSubmit}
                validationSchema={loginValidationSchema}
                validateOnBlur
                style={{ width: '100%' }}
            >
                {({ isSubmitting, errors, touched }) => (
                    <Form>
                        <Field name="email">
                            {({ field }: FieldProps<string>) => (
                                <StyledTextInput
                                    {...field}
                                    InputLabelProps={{ shrink: true }}
                                    id="email"
                                    size="medium"
                                    error={!!errors.email && touched.email}
                                    helperText={
                                        !!errors.email &&
                                        touched.email &&
                                        errors.email
                                    }
                                    label="Email"
                                    placeholder="Email"
                                />
                            )}
                        </Field>

                        <Field name="password">
                            {({ field }: FieldProps<string>) => (
                                <StyledTextInputWithSubtext
                                    {...field}
                                    InputLabelProps={{ shrink: true }}
                                    id="password"
                                    size="medium"
                                    type={showPassword ? 'text' : 'password'}
                                    error={
                                        !!errors.password && touched.password
                                    }
                                    helperText={
                                        !!errors.password &&
                                        touched.password &&
                                        errors.password
                                    }
                                    label="Password"
                                    placeholder="Password"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={
                                                        handleClickShowPassword
                                                    }
                                                    onMouseDown={
                                                        handleMouseDownPassword
                                                    }
                                                    edge="end"
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        </Field>
                        <FormHelperText>
                            <StyledLink
                                href={commonRouteConfig.forgotPassword.path}
                            >
                                Forgot password
                            </StyledLink>
                        </FormHelperText>

                        <StyledButtonContainer>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                variant="contained"
                            >
                                Sign In
                            </Button>
                        </StyledButtonContainer>

                        {subdomain === 'recruiter' && (
                            <>
                                <Divider sx={{ my: 3 }}>or</Divider>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    href={`${config.api.uri}/auth/sso`}
                                >
                                    Sign in with SSO
                                </Button>
                            </>
                        )}

                        {subdomain === 'candidate' && (
                            <StyledSubtext>
                                <Typography variant="body2">
                                    Need To Create An Account?
                                </Typography>
                                <StyledLink
                                    href={commonRouteConfig.register.path}
                                >
                                    Sign Up
                                </StyledLink>
                            </StyledSubtext>
                        )}
                    </Form>
                )}
            </Formik>
        </React.Fragment>
    )
}

export default LoginForm
